/**
 * preorder.js — MenaHub pre-order storefront integration
 * v1.0.0
 *
 * INERT for products without an active preorder_config. Defensively
 * fails silent on every error path so the existing add-to-cart flow
 * always works as a fallback.
 *
 * For configured products:
 *   - Slots available: paint the existing button as a Pre-order CTA
 *     and inject a hidden _preorder=true line-item property.
 *   - Cap full:        replace the button area with a small waitlist
 *     email-capture form posting to /api/preorder_waitlist.php.
 *
 * All UI is built with createElement/textContent — never innerHTML —
 * so admin-controlled strings can never be interpreted as HTML.
 */
(function () {
  'use strict';

  var BACKEND_BASE = 'https://admin.menahub.com';

  // ---- 1. Resolve the product handle from the page ----------------
  function resolveHandle() {
    var path = window.location.pathname || '';
    var m = path.match(/\/products\/([^\/?#]+)/);
    if (m && m[1]) return m[1];
    var meta = document.querySelector('meta[name="shopify-product-handle"]');
    if (meta && meta.content) return meta.content;
    return null;
  }

  // ---- 2. Find the add-to-cart button(s) the script needs to swap -
  function findCartButtons() {
    return {
      desktop: document.querySelector('[data-add-cart], #mh-product-form button[type="submit"], button[name="add"]'),
      mobile:  document.querySelector('[data-mobile-add-cart], .mh-mobile-add-cart'),
      form:    document.querySelector('#mh-product-form, form[action*="/cart/add"]'),
    };
  }

  // ---- 2b. Hide elements that don't belong on a pre-order product —
  // the "Out of stock" availability badge and the "Buy Now" buttons.
  // The availability badge is repainted as a Pre-order pill instead
  // of removed entirely so the meta row layout stays balanced.
  function repaintAvailabilityBadge(label, color) {
    var badges = document.querySelectorAll('.menahub-stock-badge, .variant-inventory');
    badges.forEach(function (el) {
      if (!el) return;
      el.classList.remove('menahub-stock-high', 'menahub-stock-out', 'menahub-stock-low');
      el.style.background  = color;
      el.style.color       = '#FFFFFF';
      el.style.borderColor = color;
      el.textContent       = label;
    });
  }

  function hideBuyNowButtons() {
    var btns = document.querySelectorAll('[data-buy-now], [data-mobile-buy-now], .mh-btn-buy-now');
    btns.forEach(function (b) { if (b) b.style.display = 'none'; });
  }

  // Hide the standard "Delivery Information" panel and the
  // "Free delivery by {date}" CTA badge — for a pre-order product
  // the delivery date is the release date shown on the button
  // ("Ships {date}"), and the standard delivery estimate is wrong.
  function hideStandardDeliveryBlocks() {
    var sels = [
      '.mh-delivery-info-block',
      '.menahub-delivery-cta-badge',
      '.mh-delivery-row.standard',
    ];
    sels.forEach(function (s) {
      document.querySelectorAll(s).forEach(function (el) {
        if (el) el.style.display = 'none';
      });
    });
  }

  // ---- 3. Fetch status from middleware ----------------------------
  function fetchStatus(handle) {
    var url = BACKEND_BASE + '/api/preorder_status.php?handle=' + encodeURIComponent(handle);
    return fetch(url, { credentials: 'omit' })
      .then(function (r) { return r.json(); })
      .then(function (res) { return (res && res.data) || null; })
      .catch(function () { return null; });
  }

  // ---- 4. Paint the pre-order button (no innerHTML) ---------------
  // The button gets column flex layout so the title row (icon + label)
  // and the subtitle ("Ships {date}") stack vertically — relying on
  // display:block alone fails when the host button uses flex row.
  function paintPreorderButton(btn, label, releaseDate) {
    if (!btn) return;
    btn.disabled = false;
    btn.removeAttribute('disabled');
    btn.classList.add('mh-preorder-btn');
    btn.style.background     = '#E28534';
    btn.style.color          = '#FFFFFF';
    btn.style.borderColor    = '#E28534';
    btn.style.opacity        = '1';
    btn.style.cursor         = 'pointer';
    btn.style.display        = 'inline-flex';
    btn.style.flexDirection  = 'column';
    btn.style.alignItems     = 'center';
    btn.style.justifyContent = 'center';
    btn.style.gap            = '2px';
    btn.style.lineHeight     = '1.2';
    btn.style.padding        = btn.style.padding || '12px 18px';

    // Clear existing children safely.
    while (btn.firstChild) btn.removeChild(btn.firstChild);

    // Top row — icon + main label inline together.
    var titleRow = document.createElement('span');
    titleRow.style.cssText =
      'display:inline-flex;align-items:center;gap:6px;font-weight:700';
    var icon = document.createElement('i');
    icon.className = 'fa-solid fa-clock-rotate-left';
    titleRow.appendChild(icon);
    titleRow.appendChild(document.createTextNode(label || 'Pre-order'));
    btn.appendChild(titleRow);

    // Bottom row — subtitle on its own line.
    if (releaseDate) {
      var sub = document.createElement('span');
      sub.style.cssText =
        'font-size:11px;font-weight:400;opacity:0.92;letter-spacing:0.2px';
      sub.textContent = 'Ships ' + releaseDate;
      btn.appendChild(sub);
    }
  }

  // ---- 5. Inject hidden _preorder=true line item property --------
  function injectPreorderProp(form) {
    if (!form) return;
    if (form.querySelector('input[name="properties[_preorder]"]')) return;
    var input = document.createElement('input');
    input.type  = 'hidden';
    input.name  = 'properties[_preorder]';
    input.value = 'true';
    form.appendChild(input);
  }

  // ---- 6. Waitlist form (built with safe DOM methods) ------------
  function buildEl(tag, attrs, styles) {
    var el = document.createElement(tag);
    if (attrs) {
      Object.keys(attrs).forEach(function (k) {
        if (k === 'class')   el.className = attrs[k];
        else if (k === 'text') el.textContent = attrs[k];
        else                 el.setAttribute(k, attrs[k]);
      });
    }
    if (styles) el.style.cssText = styles;
    return el;
  }

  function renderWaitlist(host, status, handle) {
    if (!host) return;

    var wrap = buildEl('div', { 'class': 'mh-preorder-waitlist' },
      'border:2px dashed #E28534;border-radius:12px;padding:16px;margin:12px 0;background:#FFF8F0;font-family:inherit');

    var heading = buildEl('div', null,
      'font-weight:700;color:#073451;margin-bottom:6px;display:flex;align-items:center;gap:8px');
    var bell = buildEl('i', { 'class': 'fa-solid fa-bell' });
    heading.appendChild(bell);
    heading.appendChild(document.createTextNode(' ' + (status.sold_out_message || 'Pre-order full. Join the waitlist.')));
    wrap.appendChild(heading);

    var sub = buildEl('div', { text: 'We will email you the moment a slot opens.' },
      'font-size:12px;color:#6B7280;margin-bottom:10px');
    wrap.appendChild(sub);

    var form = buildEl('form', { 'class': 'mh-preorder-wl-form' },
      'display:flex;gap:8px;flex-wrap:wrap');

    var emailInput = buildEl('input',
      { type: 'email', required: 'required', placeholder: 'your@email.com' },
      'flex:1;min-width:180px;padding:10px 12px;border:1px solid #E2E8F0;border-radius:8px;font-size:14px');
    form.appendChild(emailInput);

    var submitBtn = buildEl('button', { type: 'submit', text: 'Notify me' },
      'background:#073451;color:#fff;border:none;border-radius:8px;padding:10px 18px;font-weight:600;font-size:14px;cursor:pointer');
    form.appendChild(submitBtn);

    wrap.appendChild(form);

    var msg = buildEl('div', { 'class': 'mh-preorder-wl-msg' },
      'font-size:12px;color:#059669;margin-top:8px;display:none');
    wrap.appendChild(msg);

    // Hide the original button rather than removing it, so a script
    // failure leaves the page in a recoverable state.
    host.parentNode.insertBefore(wrap, host);
    host.style.display = 'none';

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var email = emailInput.value.trim();
      if (!email) return;
      submitBtn.disabled = true;
      submitBtn.textContent = 'Submitting…';
      fetch(BACKEND_BASE + '/api/preorder_waitlist.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ handle: handle, email: email }),
      }).then(function (r) { return r.json(); })
        .then(function (res) {
          if (res && res.success) {
            form.style.display = 'none';
            msg.style.display = 'block';
            msg.textContent = res.message || 'Added to waitlist.';
          } else {
            submitBtn.disabled = false;
            submitBtn.textContent = 'Notify me';
            msg.style.display = 'block';
            msg.style.color = '#DC2626';
            msg.textContent = (res && res.message) || 'Could not add you to the waitlist.';
          }
        })
        .catch(function () {
          submitBtn.disabled = false;
          submitBtn.textContent = 'Notify me';
          msg.style.display = 'block';
          msg.style.color = '#DC2626';
          msg.textContent = 'Network error. Please try again.';
        });
    });
  }

  // ---- 7. Main entry ----------------------------------------------
  function run() {
    var handle = resolveHandle();
    if (!handle) return;

    fetchStatus(handle).then(function (status) {
      if (!status || !status.enabled) return;

      var btns = findCartButtons();

      // Buy Now is irrelevant for a pre-order — hide it regardless of
      // whether slots are available or the cap is full. Same for the
      // standard delivery-date estimate — pre-order date overrides.
      hideBuyNowButtons();
      hideStandardDeliveryBlocks();

      if (status.available) {
        // Slots remain — repaint the "Out of stock" badge as a
        // brand-orange "Pre-order" pill, swap the disabled button to
        // an active Pre-order CTA, and inject the line-item property.
        repaintAvailabilityBadge('Pre-order', '#E28534');
        injectPreorderProp(btns.form);
        var dateLabel = status.expected_release_date
          ? new Date(status.expected_release_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
          : '';
        paintPreorderButton(btns.desktop, status.button_label || 'Pre-order', dateLabel);
        paintPreorderButton(btns.mobile,  status.button_label || 'Pre-order', dateLabel);
      } else {
        // Cap reached — repaint the badge as a navy "Waitlist" pill
        // and swap the button area for the email-capture form.
        repaintAvailabilityBadge('Waitlist', '#073451');
        var host = btns.desktop || btns.mobile;
        renderWaitlist(host, status, handle);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();
